import React from "react";
import { ThumbsUp, ThumbsDown, ShieldCheck } from "lucide-react";

// Read-only seller reputation row: 👍 good · 👎 bad · ✓ N verified authentic sold.
// Renders nothing when there's no data (caller can still wrap for spacing).
export function RepRow({ good = 0, bad = 0, verified = 0, size = 13, className = "", testid }) {
  const g = Number(good) || 0, b = Number(bad) || 0, v = Number(verified) || 0;
  if (!g && !b && !v) return null;
  return (
    <span className={`inline-flex items-center gap-3 ${className}`} data-testid={testid}>
      <span className="inline-flex items-center gap-1" style={{ color: "#16A34A" }} title="Good reviews">
        <ThumbsUp size={size} strokeWidth={1.9} />
        <span className="text-xs font-medium">{g}</span>
      </span>
      <span className="inline-flex items-center gap-1" style={{ color: "#E11D48" }} title="Bad reviews">
        <ThumbsDown size={size} strokeWidth={1.9} />
        <span className="text-xs font-medium">{b}</span>
      </span>
      {v > 0 && (
        <span className="inline-flex items-center gap-1" style={{ color: "#2563EB" }} title="Verified authentic items sold">
          <ShieldCheck size={size} strokeWidth={1.9} />
          <span className="text-xs font-medium">{v} verified</span>
        </span>
      )}
    </span>
  );
}

// Interactive 👍 Good / 👎 Bad picker for leaving a review.
// When `disableBad` is set (e.g. the order was fully refunded) only positive feedback can be left.
export function SentimentPicker({ value, onChange, disableBad = false }) {
  return (
    <div>
      <div className="grid grid-cols-2 gap-3" data-testid="sentiment-picker">
        <button type="button" data-testid="review-good" data-active={value === "good"}
          onClick={() => onChange("good")}
          className="hairline py-4 flex flex-col items-center gap-2 transition-colors"
          style={value === "good" ? { background: "#16A34A", color: "#fff", borderColor: "#16A34A" } : {}}>
          <ThumbsUp size={26} strokeWidth={1.8} />
          <span className="text-sm font-medium">Good</span>
        </button>
        <button type="button" data-testid="review-bad" data-active={!disableBad && value === "bad"}
          disabled={disableBad}
          onClick={() => !disableBad && onChange("bad")}
          className={`hairline py-4 flex flex-col items-center gap-2 transition-colors ${disableBad ? "opacity-40 cursor-not-allowed" : ""}`}
          style={!disableBad && value === "bad" ? { background: "#E11D48", color: "#fff", borderColor: "#E11D48" } : {}}>
          <ThumbsDown size={26} strokeWidth={1.8} />
          <span className="text-sm font-medium">Bad</span>
        </button>
      </div>
      {disableBad && (
        <p className="text-[0.7rem] text-neutral-500 mt-2" data-testid="review-bad-locked">
          This purchase was fully refunded, so only positive feedback can be left.
        </p>
      )}
    </div>
  );
}

// Small inline good/bad tag for a single review row.
export function SentimentTag({ sentiment, size = 13 }) {
  const bad = sentiment === "bad";
  return (
    <span className="inline-flex items-center gap-1 text-xs font-medium"
      style={{ color: bad ? "#E11D48" : "#16A34A" }}>
      {bad ? <ThumbsDown size={size} strokeWidth={1.9} /> : <ThumbsUp size={size} strokeWidth={1.9} />}
      {bad ? "Bad" : "Good"}
    </span>
  );
}
